import { useTranslation } from "react-i18next";

import { EReputationMatchResult, PersonProfile } from "src/core/types/entity";
import { toSnakeCase } from "src/core/utils/text";

type InformationsPPHSectionProps = {
  selectedMatch: EReputationMatchResult;
};

export const InformationsPPHSection = ({ selectedMatch }: InformationsPPHSectionProps) => {
  const { t } = useTranslation();

  const detail = selectedMatch?.detail as PersonProfile | undefined;

  const isGender = detail?.gender;
  const isNames = !!detail?.names?.length;
  const isBirthDates = !!detail?.birthDates?.length;
  const isBirthPlaces = !!detail?.birthPlaces?.length;
  const isCountryDetails = !!detail?.countryDetails?.length;
  const isAddresses = !!detail?.addresses?.length;

  return (
    <div className="flex flex-row p-10 gap-8">
      {/* Colonne gauche */}
      <div className="flex flex-col w-1/2 space-y-[10px]">
        {/* Genre */}
        {isGender ? (
          <div>
            <span>{t("modal.entityDetails.sections.informations.gender")} : </span>
            <span className="font-light">
              {t(`modal.entityDetails.sections.informations.${toSnakeCase(detail?.gender ?? "")}`)}
            </span>
          </div>
        ) : (
          <div>
            <span>{t("modal.entityDetails.sections.informations.gender")} : </span>
            <span className="font-light">
              {t("modal.entityDetails.sections.informations.unknown")}
            </span>
          </div>
        )}

        {/* Noms par groupe */}
        {isNames &&
          detail?.names?.map((group, index) => (
            <div key={`group-${index}`}>
              <div className="mb-1">
                {t(`modal.entityDetails.sections.informations.${toSnakeCase(group.type)}`, {
                  count: group.names.length,
                })}
                &nbsp;:
              </div>

              <div className="space-y-[5px] ml-4">
                {group.names.map((name, nameIdx) => {
                  const fullName = [name.firstName, name.middleName, name.surname]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <div key={`name-${index}-${nameIdx}`}>
                      <ul className="list-disc list-inside">
                        <li className="font-light">{fullName}</li>
                      </ul>

                      {name.originalScripts && name.originalScripts.length > 0 && (
                        <div className="ml-6 mt-1 space-y-1 font-light">
                          {name.originalScripts.map((os, osIdx) => (
                            <div key={`script-${index}-${nameIdx}-${osIdx}`}>
                              {os.name} {os.langCode && <span>({os.langCode})</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>

      {/* Colonne droite */}
      <div className="flex flex-col w-1/2 border-l border-border pl-6 space-y-[10px]">
        {/* Dates de naissance, de décès, etc. */}
        {isBirthDates && (
          <div>
            <div className="space-y-4">
              {detail?.birthDates?.map((group, index) => (
                <div key={index}>
                  <div className="mb-1">
                    {t(`modal.entityDetails.sections.informations.${toSnakeCase(group.type)}`, {
                      count: group.dates.length,
                    })}
                    &nbsp;:
                  </div>
                  <ul className="list-disc list-inside space-y-1 ml-4 font-light">
                    {group.dates.map((d: string, i: number) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Lieux de naissance */}
        {isBirthPlaces && (
          <div>
            <div className="mb-1">
              {t("modal.entityDetails.sections.informations.birth_place", {
                count: detail?.birthPlaces?.length,
              })}
              &nbsp;:
            </div>
            <ul className="ml-4 list-disc list-inside font-light space-y-1">
              {detail?.birthPlaces?.map((bp, index) => (
                <li key={`birth-place-${index}`}>
                  {bp.place} ({bp.country})
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Détails pays */}
        {isCountryDetails && (
          <>
            {detail?.countryDetails?.map((countryDetail, index) => (
              <div key={`country-detail-${index}`}>
                <div>
                  {t(
                    `modal.entityDetails.sections.informations.${toSnakeCase(countryDetail.type)}`,
                    {
                      count: countryDetail.countries.length,
                    }
                  )}
                  &nbsp;:
                </div>
                <ul className="ml-4 list-disc list-inside font-light space-y-1">
                  {countryDetail.countries.map((country, countryIndex) => (
                    <li key={`country-detail-${index}-country-${countryIndex}`}>{country}</li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        )}
        {/* Adresses */}
        {isAddresses && (
          <div>
            <div className="mb-1">
              {t("modal.entityDetails.sections.informations.addresses", {
                count: detail?.addresses?.length,
              })}
              &nbsp;:
            </div>
            <ul className="ml-4 list-disc list-inside font-light space-y-1">
              {detail?.addresses?.map((addr, index) => {
                const parts = [addr.addressLine, addr.city, addr.region, `${addr.country}`].filter(
                  Boolean
                );

                return <li key={`addr-${index}`}>{parts.join(", ")}</li>;
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
